"use client"

import type React from "react"
import {
    Dialog,
    DialogContent,
    IconButton,
    Typography,
    Grid,
    Box,
    Button,
    Chip,
    useMediaQuery,
    useTheme,
    Divider,
    Tooltip,
} from "@mui/material"
import { IconX, IconBrandWhatsapp, IconInfoCircle } from "@tabler/icons-react"
import { type Product, getStatusText } from "../types"
import ImageCarousel from "./ImageCarousel"

interface ProductDetailModalProps {
    product: Product
    onClose: () => void
    onInfoClick: () => void
    onTagClick: (tag: string) => void
}

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({ product, onClose, onInfoClick, onTagClick }) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("md"))
    const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"))

    const handleWhatsAppClick = () => {
        const message = encodeURIComponent(
            `Hola, estoy interesado/a en el cuadrito "${product.name}" (ID: ${product.id}). ¿Está disponible?`,
        )

        window.open(`https://wa.me/59174318810?text=${message}`, "_blank")
    }

    const handleTagClick = (tag: string) => {
        onTagClick(tag)
        onClose()
    }

    const statusText = getStatusText(product.status)

    return (
        <Dialog
            open={true}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallMobile}
            PaperProps={{
                sx: {
                    borderRadius: isSmallMobile ? 0 : 2,
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                    maxHeight: isSmallMobile ? "100%" : "90vh",
                    margin: isSmallMobile ? 0 : 2,
                },
            }}
        >
            <IconButton
                onClick={onClose}
                sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "white",
                    backgroundColor: theme.palette.secondary.main,
                    "&:hover": {
                        backgroundColor: theme.palette.primary.main,
                    },
                    zIndex: 1,
                }}
            >
                <IconX size={20} />
            </IconButton>

            <DialogContent sx={{ p: { xs: 1.5, md: 3 } }}>
                <Grid container spacing={isMobile ? 2 : 3}>
                    <Grid item xs={12} md={6}>
                        <ImageCarousel
                            images={product.imageUrls}
                            enableFullscreen={true} // Habilitamos el fullscreen en el modal
                            showInfoButton={false} // Quitamos el botón de info del carrusel
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                            <Typography
                                variant="h4"
                                component="h2"
                                color="primary"
                                sx={{
                                    mb: 2,
                                    fontWeight: 600,
                                    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                                }}
                            >
                                {product.name}
                            </Typography>

                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography
                                    variant="h5"
                                    component="span"
                                    color="secondary"
                                    sx={{
                                        fontWeight: "bold",
                                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                                    }}
                                >
                                    {product.price} Bs.
                                </Typography>

                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Chip
                                        label={statusText}
                                        sx={{
                                            backgroundColor: product.status === "ready" ? "#0d6336" : "#3c2a20",
                                            color: "white",
                                            fontWeight: "bold",
                                            fontSize: "0.8rem",
                                            px: 1,
                                        }}
                                    />
                                    <Tooltip title="Información sobre estados">
                                        <IconButton size="small" onClick={onInfoClick} sx={{ ml: 0.5, color: "#0d6336" }}>
                                            <IconInfoCircle size={18} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={1} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Tiempo de entrega:</strong>
                                    </Typography>
                                    <Typography variant="body1">{product.deliveryTime}</Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Tamaño:</strong>
                                    </Typography>
                                    <Typography variant="body1">{product.size}</Typography>
                                </Grid>

                                <Grid item xs={6} sx={{ mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Categoría:</strong>
                                    </Typography>
                                    <Typography variant="body1">{product.category}</Typography>
                                </Grid>

                                <Grid item xs={6} sx={{ mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Disponibles:</strong>
                                    </Typography>
                                    <Typography variant="body1">
                                        {product.cantidad} unidad{product.cantidad !== 1 ? "es" : ""}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    <strong>Descripción:</strong>
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                    {product.description}
                                </Typography>
                            </Box>

                            {product.tags.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        <strong>Etiquetas:</strong>
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                        {product.tags.map((tag, index) => (
                                            <Chip
                                                key={index}
                                                label={tag}
                                                size="small"
                                                onClick={() => handleTagClick(tag)}
                                                sx={{
                                                    backgroundColor: "#d7ddd1",
                                                    color: "#0d6336",
                                                    cursor: "pointer",
                                                    "&:hover": {
                                                        backgroundColor: "#b8c2ae",
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleWhatsAppClick}
                                startIcon={<IconBrandWhatsapp size={24} />}
                                sx={{
                                    mt: "auto",
                                    backgroundColor: "#25D366",
                                    "&:hover": {
                                        backgroundColor: "#128C7E",
                                    },
                                    py: 1.5,
                                }}
                            >
                                Consultar por WhatsApp
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

export default ProductDetailModal

